import { CreateResult, Identifier, useCreate, useListContext, useSaveContext } from "ra-core"
import { FC, useCallback, useMemo, useRef } from "react"
import { useEncryptedDatabasesContext } from "@/components/databases/useEncryptedDatabases"
import Create from "@/controller/Create"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import { MutationCallbacks } from "@/dataProvider/types"
import { UpgradeStatus } from "@/gen/api/encrypt"
import { BulkUpdateFieldGovernanceInput, BulkUpdateInfoInput, BulkUpdateSelectedInput, BulkUpdateValidationFailedIdsResponseDto, UpdateFieldGovernanceInput } from "@/gen/api/governance"
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext"
import { FieldEditContext } from "./FieldEditContext"


export const FieldBulkEditContextProvider: FC = ({ children }) => {
  const { cancelBulkEdit } = useDataCatalogContext()
  const { refetch } = useListContext()
  const handlers = useDefaultSavedHandlers('Bulk Edit Fields')
  handlers.onSuccess.merge(useCallback(() => {
    cancelBulkEdit()
    refetch()
  }, []))

  return (
    <Create
      resource="govrnc-data-catalog-bulk"
      basePath=""
      {...handlers}
    >
      <FieldBulkEditInner>
        {children}
      </FieldBulkEditInner>
    </Create>
  )
}

const FieldBulkEditInner: FC = ({ children }) => {
  const { bulkEditEncryptRules, bulkEditMaskingKind, retrieveAllSelectedIds } = useDataCatalogContext()
  const { encryptedDbMap } = useEncryptedDatabasesContext()
  const { save, saving } = useSaveContext()
  
  const [sendValidate, { loading: validating }] = useCreate('govrnc-vali-bulk', undefined, { returnPromise: true })
  const requesting = useRef(false)
  requesting.current = validating || !!saving

  const value = useMemo(() => {
    const saveBulk = (x: BulkUpdateInfoInput, callbacks?: MutationCallbacks) => {
      if (requesting.current) { return }
      
      const ids = retrieveAllSelectedIds()
      const bulkEditInput: BulkUpdateFieldGovernanceInput = {
        selected: ids,
        bulkUpdateInfo: x,
      };
      
      // If there is encryptRule/maskingRule edit, the following validation steps should be considered:
      // * validate locked encrypted database fields | at frontend - because data_svc/govnc_svc do not have knowlege of locked dbs (encrypt_svc)
      // * validate identity/computed fields         | at backend
      const lockedDbValiResult = { lockedIds: [] as BulkUpdateSelectedInput[], unlockedIds: [] as BulkUpdateSelectedInput[] }
      
      if (x.secondaryClassificationId || x.encryptMappingStatus || x.maskingRule) {
        const lockedDbs = new Set(Object.values(encryptedDbMap || {}).filter(x => x.upgradeStatus !== UpgradeStatus.Unlocked).map(x => x.databaseId || 0));
        ids.reduce((acc, id) => {
          if (lockedDbs.has(id.databaseId as number)) {
            acc.lockedIds.push(id)
          } else {
            acc.unlockedIds.push(id)
          }
          return acc
        }, lockedDbValiResult);
      }
      
      if (x.encryptMappingStatus || x.maskingRule) {
        bulkEditInput.selected = lockedDbValiResult.unlockedIds;
        (sendValidate({payload: {data: {bulkEditInput, lockedIds: lockedDbValiResult.lockedIds }}}) as Promise<CreateResult<BulkValidationError>>)
        .then(({data}) => {
          if (data.failed.length === 0 && save) {
            save(bulkEditInput, '', callbacks)
          }
        })
      } else if (lockedDbValiResult.lockedIds.length > 0) {
        bulkEditInput.selected = []
        sendValidate({payload: {data: {bulkEditInput, lockedIds: lockedDbValiResult.lockedIds }}})
      } else {
        delete bulkEditInput.bulkUpdateInfo?.encryptMappingStatus
        save && save(bulkEditInput, '', callbacks)
      }
    }
    return {
      save: saveBulk,
      encryptRules: bulkEditEncryptRules,
      maskingKind: bulkEditMaskingKind.kind,
      mode: 'bulk' as const,
    }
  }, [bulkEditEncryptRules, bulkEditMaskingKind, encryptedDbMap, retrieveAllSelectedIds, sendValidate, save])
  
  return (
    <FieldEditContext.Provider value={value}>
      {children}
    </FieldEditContext.Provider>
  )
}

export interface BulkValidationError {
  id: Identifier,
  failed: BulkUpdateValidationFailedIdsResponseDto[],
}
